import { __awaiter } from 'tslib';
import * as Long from 'long';
import { im_proto } from '../proto/index.js';
import { IMEvent } from '../core/consts.js';
import { Logger } from '../core/metrics/logger.js';
import '../core/metrics/monitor.js';
import { ServiceIdentity } from '../core/service/RuntimeContext.js';
import '../core/network/NetworkItem.js';
import { ConversationCoreInfo } from '../core/model/ConversationCoreInfo.js';
import { ConversationSettingInfo } from '../core/model/ConversationSettingInfo.js';
import 'moize';
import { Message } from '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import '../adapter/manager/AdapterManager.js';
import '../adapter/manager/types.js';
import '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import '../core/plugin/DbProxy.js';
import '../core/metrics/trackerTypes.js';
import { BasePlugin } from '../core/plugin/BasePlugin.js';
import '../core/model/CommandMessage.js';
import { StrangerApiManager } from './api/StrangerApiManager.js';
import { StrangerConversation } from './model/StrangerConversation.js';
import { ModeChangeProcessor } from './processor/ModeChangeProcessor.js';

class StrangerPlugin extends BasePlugin {
    constructor() {
        super(...arguments);
        this.internalUnreadCount = 0;
    }
    /**
     * 获取陌生人会话列表
     * @param params.cursor 游标
     * @param params.limit 单次拉取数量
     */
    getStrangerConversationList(params = {}) {
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.GetStrangerConversationList({
                cursor: params.cursor ? Long.fromValue(params.cursor) : Long.ZERO,
                limit: (_a = params.limit) !== null && _a !== void 0 ? _a : 10
            });
            const resp = (_b = response.body) === null || _b === void 0 ? void 0 : _b.get_stranger_conversation_body;
            const conv = resp.conversation_list;
            this.internalUnreadCount = resp.total_unread;
            let ignoreEvent = false;
            // 对于预览场景, 判断一下拉下来的数据是不是完全一样, 如果一样就不插入了
            if (((_c = params.cursor) === null || _c === void 0 ? void 0 : _c.toString()) === '0' && params.limit === 1) {
                const currentPreview = yield this.getStrangerPreview({
                    sync: false
                });
                if (conv &&
                    conv.length === 1 &&
                    ((_d = currentPreview === null || currentPreview === void 0 ? void 0 : currentPreview.conversation) === null || _d === void 0 ? void 0 : _d.id) === conv[0].conversation_id &&
                    ((_e = currentPreview === null || currentPreview === void 0 ? void 0 : currentPreview.message) === null || _e === void 0 ? void 0 : _e.serverId) === ((_g = (_f = conv[0].last_message) === null || _f === void 0 ? void 0 : _f.server_message_id) === null || _g === void 0 ? void 0 : _g.toString()) &&
                    (currentPreview === null || currentPreview === void 0 ? void 0 : currentPreview.unreadCount) === (resp === null || resp === void 0 ? void 0 : resp.total_unread)) {
                    ignoreEvent = true;
                }
            }
            const internalConv = conv.map(sc => {
                var _a;
                // 没有ticket, 在sendMessage的时候会自然刷新
                const c = new StrangerConversation(this.ctx);
                c.coreInfo = new ConversationCoreInfo(c); // create core/setting manually
                c.settingInfo = new ConversationSettingInfo(c);
                c.id = sc.conversation_id;
                c.shortId = sc.conversation_short_id.toString();
                c.type = im_proto.ConversationType.ONE_TO_ONE_CHAT;
                c.unreadCount = sc.unread;
                c.isOffline = false;
                c.firstPageParticipant = im_proto.ParticipantsPage.create({
                    // 用返回的participant填充firstPageParticipant
                    participants: sc.participants
                });
                c.ext = (_a = sc.ext) !== null && _a !== void 0 ? _a : {};
                if (!ignoreEvent) {
                    this.resolve(ServiceIdentity.ConversationManager).upsert(c);
                    const lastMsg = Message.fromServerMessage(this.ctx, sc.last_message, response.log_id);
                    this.resolve(ServiceIdentity.MessageManager).upsert(lastMsg);
                }
                return c;
            });
            return {
                conversation: internalConv,
                unreadCount: resp === null || resp === void 0 ? void 0 : resp.total_unread,
                hasMore: resp === null || resp === void 0 ? void 0 : resp.has_more,
                cursor: resp === null || resp === void 0 ? void 0 : resp.next_cursor
            };
        });
    }
    /**
     * 从服务端拉取陌生人会话消息
     * @param params.conversation 会话
     * @param params.resetUnreadCount 是否重置未读数量
     */
    getStrangerConversationMessage(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.GetStrangerConversationMessage({
                shortId: Long.fromString(params.conversation.shortId),
                resetUnreadCount: (_a = params.resetUnreadCount) !== null && _a !== void 0 ? _a : false
            });
            const resp = (_b = response.body) === null || _b === void 0 ? void 0 : _b.get_stranger_messages_body;
            const msgs = resp.messages;
            msgs.forEach(m => this.resolve(ServiceIdentity.MessageManager).upsert(Message.fromServerMessage(this.ctx, m, response.log_id)));
            return this.resolve(ServiceIdentity.MessageManager).getList(params.conversation.id);
        });
    }
    /**
     * 删除某一条陌生人消息
     * @param params.conversation 会话
     * @param params.message 消息
     */
    deleteStrangerMessage(params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.DeleteStrangerMessage({
                serverId: Long.fromString(params.message.serverId),
                shortId: Long.fromString(params.conversation.shortId)
            });
            this.resolve(ServiceIdentity.MessageManager).delete(params.conversation.id, params.message.serverId);
        });
    }
    /**
     * 删除某一个陌生人会话
     * @param params.conversation 会话
     */
    deleteStrangerConversation(params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.DeleteStrangerConversation({
                shortId: Long.fromString(params.conversation.shortId)
            });
            this.resolve(ServiceIdentity.ConversationManager).delete(params.conversation.id);
        });
    }
    /**
     *  删除所有陌生人会话
     */
    deleteAllStrangerConversation() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.DeleteAllStrangerConversation();
            const strangerConv = this.resolve(ServiceIdentity.ConversationManager).getConversationArray(c => c.mode !== 0);
            strangerConv.forEach(c => this.resolve(ServiceIdentity.ConversationManager).delete(c.id));
            this.internalUnreadCount = 0;
        });
    }
    /**
     * 标记陌生人会话为已读
     * @param params.conversation 会话
     */
    markStrangerConversationRead(params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.MarkStrangerConversationRead({
                shortId: Long.fromString(params.conversation.shortId)
            });
            this.internalUnreadCount -= params.conversation.unreadCount;
            params.conversation.unreadCount = 0;
            this.resolve(ServiceIdentity.ConversationManager).upsert(params.conversation);
        });
    }
    /**
     * 标记所有陌生人会话为已读
     */
    markAllStrangerConversationRead() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.MarkAllStrangerConversationRead();
            const strangerConv = this.resolve(ServiceIdentity.ConversationManager).getConversationArray(c => c.mode !== 0);
            strangerConv.forEach((c) => {
                c.unreadCount = 0;
                this.resolve(ServiceIdentity.ConversationManager).upsert(c);
            });
            this.internalUnreadCount = 0;
        });
    }
    /**
     *  获取当前陌生人会话总未读数
     */
    getStrangerTotalUnread() {
        return this.internalUnreadCount;
    }
    /**
     * 获取陌生人会话预览（最近一个会话+最近一条消息+总未读数）
     * @param params.sync sync不填默认从本地缓存中计算，填true则会从服务端拉取最新的
     */
    getStrangerPreview(params = {}) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const c = yield this.refreshStrangerPreviewConversation((_a = params === null || params === void 0 ? void 0 : params.sync) !== null && _a !== void 0 ? _a : false);
            if (c) {
                return {
                    conversation: c,
                    message: c.lastVisibleMessage,
                    unreadCount: this.internalUnreadCount
                };
            }
            return null;
        });
    }
    /**
     *  获取本地缓存的陌生人会话
     */
    getLocalStrangerConversation() {
        return this.resolve(ServiceIdentity.ConversationManager).getConversationArray(c => c.mode !== 0);
    }
    install() {
        this.api = this.register(ServiceIdentity.StrangerApi, StrangerApiManager);
        this.register(ServiceIdentity.StrangerPlugin, this);
        this.instance.getStrangerConversationList = this.extendFunc(this.getStrangerConversationList);
        this.instance.getStrangerConversationMessage = this.extendFunc(this.getStrangerConversationMessage);
        this.instance.deleteStrangerMessage = this.extendFunc(this.deleteStrangerMessage);
        this.instance.deleteStrangerConversation = this.extendFunc(this.deleteStrangerConversation);
        this.instance.deleteAllStrangerConversation = this.extendFunc(this.deleteAllStrangerConversation);
        this.instance.markStrangerConversationRead = this.extendFunc(this.markStrangerConversationRead);
        this.instance.markAllStrangerConversationRead = this.extendFunc(this.markAllStrangerConversationRead);
        this.instance.getStrangerTotalUnread = this.extendFunc(this.getStrangerTotalUnread);
        this.instance.getStrangerPreview = this.extendFunc(this.getStrangerPreview);
        this.instance.getLocalStrangerConversation = this.extendFunc(this.getLocalStrangerConversation);
        this.resolve(ServiceIdentity.MessageManager).injectProcessor(new ModeChangeProcessor(this.ctx));
        // 转正完成之后拉混链
        this.resolve(ServiceIdentity.EventBus).subscribe(IMEvent.StrangerUpgrade, (conv) => __awaiter(this, void 0, void 0, function* () {
            yield this.resolve(ServiceIdentity.CoreInstance).getMessagesByUser({
                inboxType: conv.inboxType
            });
        }));
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.getStrangerPreview({
                    sync: true
                });
            }
            catch (err) {
                Logger.ctxWarn(this.ctx, 'stranger plugin init error:', err);
            }
        });
    }
    receivePacket(packet) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (packet.cmd === im_proto.IMCMD.STRANGER_NEW_MSG_NOTIFY) {
                const body = (_a = packet.body) === null || _a === void 0 ? void 0 : _a.has_new_message_notify.message;
                const conv = this.resolve(ServiceIdentity.ConversationManager).getRaw(body.conversation_id);
                if (!conv) {
                    return;
                }
                const msg = Message.fromServerMessage(this.ctx, body, packet.log_id);
                this.resolve(ServiceIdentity.MessageManager).upsert(msg);
                this.internalUnreadCount++;
                this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ReceiveNewStrangerMessage, this, msg);
            }
        });
    }
    ticker() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.ctx.option.pullInterval) {
                yield this.refreshStrangerPreviewConversation(true);
            }
        });
    }
    refreshStrangerPreviewConversation(sync = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (sync) {
                yield this.getStrangerConversationList({
                    cursor: Long.ZERO,
                    limit: 1
                });
            }
            const convs = this.resolve(ServiceIdentity.ConversationManager).getConversationArray(c => c.mode !== 0);
            for (const c of convs) {
                if (c.lastVisibleMessage) {
                    return c;
                }
            }
            return null;
        });
    }
}

export { StrangerPlugin };
//# sourceMappingURL=StrangerPlugin.js.map
